import { DateTime } from 'luxon';
import { Button, Input } from '@flixbus/honeycomb-react';
import { toISODate } from '../../../../helpers/dateTime';

import { useVoucherFormContext } from '../../Context';

import { BlackoutPeriodRule } from '../../../../types/Rules';
import { Icon, IconClose } from '@flixbus/honeycomb-icons-react';

const BlackoutFrom = ({ index }: { index: number }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  // Get the specific blackout period rule for this index
  const ruleValue = rules.find(
    r => r.type === 'blackout_period' && (r as BlackoutPeriodRule).id === `blackout_${index}`,
  ) as BlackoutPeriodRule | undefined;

  // Get the previous blackout period rule (index - 1)
  const previousRuleValue =
    index > 0
      ? (rules.find(
          r =>
            r.type === 'blackout_period' &&
            (r as BlackoutPeriodRule).id === `blackout_${index - 1}`,
        ) as BlackoutPeriodRule | undefined)
      : undefined;

  const dateObject = ruleValue?.start ? DateTime.fromISO(ruleValue.start) : null;

  // Calculate the minimum start date: day after previous period's end, or today if no previous period
  const minDate = previousRuleValue?.end
    ? DateTime.fromISO(previousRuleValue.end)
        .plus({ days: 1 })
        .setLocale('de')
        .toFormat('yyyy-MM-dd')
    : new Date().toISOString().split('T')[0];

  const handleClear = () => {
    const ruleId = `blackout_${index}`;

    // If there's no end date, remove the rule entirely
    if (!ruleValue?.end) {
      handleRuleChange({ type: 'blackout_period', id: ruleId } as BlackoutPeriodRule, true, ruleId);
    } else {
      // Keep the rule but remove the start date
      handleRuleChange(
        {
          type: 'blackout_period',
          id: ruleId,
          end: ruleValue.end,
        } as BlackoutPeriodRule,
        false,
        ruleId,
      );
    }
  };

  return (
    <Input
      name={`rule-blackout-from-input-${index}`}
      id={`rule-blackout-from-input-${index}`}
      label={<>From</>}
      type='date'
      min={minDate}
      required
      value={dateObject ? dateObject.setLocale('de').toFormat('yyyy-MM-dd') : ''}
      onChange={e => {
        const { value } = e.target;
        const ruleId = `blackout_${index}`;

        if (value === '') {
          // If the value is empty and there's no end date, remove the rule entirely
          if (!ruleValue?.end) {
            handleRuleChange(
              { type: 'blackout_period', id: ruleId } as BlackoutPeriodRule,
              true,
              ruleId,
            );
          } else {
            // Keep the rule but remove the start date
            handleRuleChange(
              {
                type: 'blackout_period',
                id: ruleId,
                end: ruleValue.end,
              } as BlackoutPeriodRule,
              false,
              ruleId,
            );
          }
        } else {
          // Update or create the rule with the new start date
          handleRuleChange(
            {
              type: 'blackout_period',
              id: ruleId,
              start: toISODate(DateTime.fromJSDate(new Date(value))),
              end: ruleValue?.end,
            } as BlackoutPeriodRule,
            false,
            ruleId,
          );
        }
      }}
      iconRight={
        dateObject ? (
          <Button aria-label='Clear start date field' onClick={handleClear}>
            <Icon appearance='primary' InlineIcon={IconClose} />
          </Button>
        ) : undefined
      }
    />
  );
};

export default BlackoutFrom;
